import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppShell,
  Callout,
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  type CurrencyOption,
  type EntryCurrency,
  type SearchHit,
} from '@biovet/ui';
import { MODULES, visibleModules, withBadges } from './nav.js';
import { HOME, moduleOf, useRoute, withHrefs } from './lib/router.js';
import { ChartOfAccountsScreen } from './pages/ChartOfAccounts.js';
import { GeneralLedgerScreen } from './pages/GeneralLedger.js';
import { TrialBalanceScreen } from './pages/TrialBalance.js';
import { BalanceSheetScreen, ProfitAndLossScreen } from './pages/Statements.js';
import { VoucherEntryScreen } from './pages/VoucherEntry.js';
import { ChangePasswordScreen } from './pages/ChangePassword.js';
import { LoginScreen } from './pages/Login.js';
import { VoucherDetailModal } from './pages/VoucherDetailModal.js';
import { DashboardScreen } from './pages/Dashboard.js';
import { RecordsScreen } from './pages/Records.js';
import { DealEntryScreen } from './pages/DealEntry.js';
import { DealPartiesScreen } from './pages/DealParties.js';
import {
  AvailabilityScreen,
  CommissionInvoiceScreen,
  DealRegisterScreen,
  PendingSettlementScreen,
} from './pages/Brokerage.js';
import { CommissionRunScreen, SaleReturnScreen, SalesInvoiceScreen } from './pages/Sales.js';
import {
  ImportCostingScreen,
  ImportShipmentsScreen,
  PurchaseEntryScreen,
  PurchaseReturnScreen,
} from './pages/Purchase.js';
import {
  BatchExpiryScreen,
  PhysicalCountScreen,
  ProductsScreen,
  StockLedgerScreen,
  StockTransferScreen,
  WarehouseStockScreen,
} from './pages/Inventory.js';
import { ArAgeingScreen, ApAgeingScreen, CashPositionScreen } from './pages/CashAgeing.js';
import {
  ArApAgeingScreen,
  AuditTrailScreen,
  GrossMarginScreen,
  PurchaseRegisterScreen,
  SalesByZoneScreen,
  SalesRegisterScreen,
} from './pages/ManagementReports.js';
import { RecentActivityScreen } from './pages/RecentActivity.js';
import { BankReconciliationScreen } from './pages/BankReconciliation.js';
import { UsersZonesScreen } from './pages/UsersZones.js';
import { CategoriesScreen } from './pages/Categories.js';
import { PartiesScreen } from './pages/Parties.js';
import { SetupScreen, type SetupKey } from './pages/Setup.js';
import { ReportsScreen } from './pages/Reports.js';
import { RECORD_SETS } from './lib/design-data.js';
import { isVoucherTypeCode, type VoucherTypeCode } from './lib/voucher-config.js';
import {
  companyLocation,
  companyToday,
  defaultAccountOfKind,
  setAccounts,
  setCompany,
  setCurrencies,
  setFiscalCalendar,
  setUoms,
  type Account,
  type PeriodStatus,
} from './lib/reference-data.js';
import { forgetSignIn, hasSignInHint, rememberSignIn } from './lib/session.js';
import {
  apiGet,
  apiSend,
  useApiBusy,
  useCan,
  useResource,
  type ApiAccount,
  type ApiContext,
  type NavCounts,
  type Session,
} from './lib/api.js';
import type { Role } from './lib/voucher-model.js';

export function App() {
  const [signedIn, setSignedIn] = useState<boolean>(hasSignInHint);

  const [epoch, setEpoch] = useState(0);

  const signIn = () => {
    rememberSignIn();
    setSignedIn(true);
  };

  const signOut = () => {
    forgetSignIn();
    setSignedIn(false);
    setEpoch((n) => n + 1);
  };

  if (!signedIn) return <LoginScreen onSignIn={signIn} />;
  return (
    <Book
      key={epoch}
      onViewChanged={() => setEpoch((n) => n + 1)}
      onSignOut={signOut}
      onSessionRejected={signOut}
    />
  );
}

function Book({
  onViewChanged,
  onSignOut,
  onSessionRejected,
}: {
  onViewChanged: () => void;
  onSignOut: () => void;
  onSessionRejected: () => void;
}) {
  const { route, navigate: go } = useRoute();
  const { module, item } = route;
  const [ledgerAccount, setLedgerAccount] = useState('');
  const [dealNo, setDealNo] = useState<string | null>(null);
  const [shipmentNo, setShipmentNo] = useState<string | null>(null);
  const [currency, setCurrency] = useState<EntryCurrency>('');
  const [rates, setRates] = useState<Record<string, string>>({});
  const [branchCode, setBranchCode] = useState('HO');
  const [openVoucher, setOpenVoucher] = useState<{ type: string; no: string } | null>(null);

  const sessionRes = useResource<Session>('/session');
  const contextRes = useResource<ApiContext>('/context');
  const accountsRes = useResource<{ accounts: ApiAccount[] }>('/accounts');
  const session = sessionRes.data;
  const can = useCan(session);

  const impersonator = Boolean(session?.isSuperAdmin) || Boolean(session?.viewingAs);
  const usersRes = useResource<{ users: DirectoryUser[] }>(impersonator ? '/users' : null);
  const viewAs = useMemo(
    () =>
      impersonator
        ? (usersRes.data?.users ?? []).map((u) => ({
            value: u.email,
            label: u.roleName,
            hint: `${u.fullName} · ${u.scopeName}`,
          }))
        : undefined,
    [impersonator, usersRes.data],
  );

  const switchView = useCallback(
    (target: string) => {
      void apiSend('POST', '/session/view-as', { email: target }).then(
        onViewChanged,
        () => sessionRes.reload(),
      );
    },
    [onViewChanged, sessionRes],
  );

  const accounts = useMemo<Account[]>(() => {
    const list = (accountsRes.data?.accounts ?? []).map(toAccount);
    setAccounts(list);
    return list;
  }, [accountsRes.data]);

  const defaultTill = useMemo(() => defaultAccountOfKind('CASH'), [accounts]);

  const handleSignOut = useCallback(() => {
    void apiSend('POST', '/logout').finally(onSignOut);
  }, [onSignOut]);

  const currencies = useMemo(() => contextRes.data?.currencies ?? [], [contextRes.data]);
  const baseCurrency = contextRes.data?.company.baseCurrency ?? '';

  setCurrencies(currencies);
  setUoms(contextRes.data?.uoms ?? []);

  {
    const ctx = contextRes.data;
    setCompany(ctx ? ctx.company : null);
    setFiscalCalendar(
      ctx?.fiscalYear ?? null,
      (ctx?.periods ?? []).map((p) => ({
        periodNo: p.periodNo,
        name: p.name,
        startDate: p.startDate,
        endDate: p.endDate,
        status: p.status as PeriodStatus,
        isAdjustment: p.isAdjustment,
      })),
    );
  }

  useEffect(() => {
    if (currencies.length === 0) return;
    setRates((prior) => {
      const next = { ...prior };
      for (const c of currencies) if (c.rate && next[c.code] === undefined) next[c.code] = c.rate;
      return next;
    });
    setCurrency((prior) =>
      prior && currencies.some((c) => c.code === prior) ? prior : (baseCurrency ?? ''),
    );
  }, [currencies, baseCurrency]);

  const rateFor = useCallback(
    (code: string): string => (code === baseCurrency ? '1' : (rates[code] ?? '')),
    [baseCurrency, rates],
  );

  const applyRate = useCallback(
    async (code: EntryCurrency, rate: string) => {
      await apiSend('POST', '/setup/fx-rates', {
        rateDate: companyToday(),
        fromCurrency: code,
        toCurrency: baseCurrency,
        rateType: 'SPOT',
        rate,
        source: 'Top bar',
      });
      contextRes.reload();
    },
    [baseCurrency, contextRes],
  );

  useEffect(() => {
    const preferred = session?.branches.find((b) => b.isDefault) ?? session?.branches[0];
    if (preferred) setBranchCode(preferred.code);
  }, [session]);

  useEffect(() => {
    if (sessionRes.status === 401 || sessionRes.status === 404) onSessionRejected();
  }, [sessionRes.status, onSessionRejected]);

  const visibleNav = useMemo(() => visibleModules(MODULES, can), [can]);

  const navCountsRes = useResource<NavCounts>('/nav-counts');
  const reloadNavCounts = navCountsRes.reload;
  useEffect(() => {
    reloadNavCounts();
  }, [item, reloadNavCounts]);

  const modules = useMemo(
    () => withHrefs(withBadges(visibleNav, navCountsRes.data)),
    [visibleNav, navCountsRes.data],
  );

  useEffect(() => {
    if (!session) return;
    if (!modules.some((m) => m.key === module)) {
      const first = modules[0];
      if (first) {
        go({ module: first.key, item: first.items[0]?.key ?? HOME.item }, { replace: true });
      }
    }
  }, [session, modules, module, go]);

  const handleModuleChange = (key: string) => {
    const first = modules.find((m) => m.key === key)?.items[0];
    if (first) go({ module: key, item: first.key });
  };

  const navigate = (nextModule: string, nextItem: string) => {
    go({ module: nextModule, item: nextItem });
  };

  const openLedger = (code: string) => {
    setLedgerAccount(code);
    navigate('finance', 'gl');
  };

  const openDeal = (no: string) => {
    setDealNo(no);
    navigate('brokerage', 'deal');
  };

  const openCosting = (no: string) => {
    setShipmentNo(no);
    navigate('sales', 'costing');
  };

  const openItem = (key: string) => {
    const owner = moduleOf(key);
    if (owner) navigate(owner, key);
  };

  const busy = useApiBusy();

  const isAdmin = Boolean(session?.isSuperAdmin);
  const canSwitchBranch = isAdmin || can('admin.zone', 'VIEW');

  const searchHits = useCallback(async (query: string): Promise<SearchHit[]> => {
    const { hits } = await apiGet<{ hits: ApiSearchHit[] }>('/search', { q: query });
    return hits.map((hit) => ({
      id: `${hit.kind}:${hit.voucherType ?? ''}:${hit.key}`,
      label: hit.label,
      hint: hit.hint ?? undefined,
      group: GROUP_LABEL[hit.kind],
    }));
  }, []);

  const openSearchHit = (hit: SearchHit) => {
    const [kind, qualifier, ...rest] = hit.id.split(':');
    const key = rest.join(':');

    if (kind === 'nav') openItem(key);
    else if (kind === 'account') openLedger(key);
    else if (kind === 'voucher' && qualifier) setOpenVoucher({ type: qualifier, no: key });
  };

  if (session?.mustChangePassword) {
    return (
      <ChangePasswordScreen
        fullName={session.fullName}
        onChanged={() => {
          sessionRes.reload();
          accountsRes.reload();
          contextRes.reload();
        }}
        onSignOut={handleSignOut}
      />
    );
  }

  return (
    <AppShell
      brand={{
        name: contextRes.data?.company.name ?? 'biovet',
        tagline: contextRes.data?.company.tagline ?? undefined,
        mark: contextRes.data?.company.brandMark ?? undefined,
        logoUrl: contextRes.data?.company.logoUrl ?? undefined,
        timezone: contextRes.data?.company.timezone ?? undefined,
      }}
      letterhead={
        contextRes.data
          ? {
              name: contextRes.data.company.name,
              legalName: contextRes.data.company.legalName,
              logoUrl: contextRes.data.company.logoUrl ?? undefined,
              location: companyLocation(),
              phone: contextRes.data.company.phone ?? undefined,
              email: contextRes.data.company.email ?? undefined,
              ntn: contextRes.data.company.ntn ?? undefined,
              strn: contextRes.data.company.strn ?? undefined,
              printedAt: companyToday(),
            }
          : undefined
      }
      modules={modules}
      activeModule={module}
      onModuleChange={handleModuleChange}
      activeItem={item}
      onItemChange={(key) => navigate(module, key)}
      busy={busy}
      user={{
        name: session?.fullName ?? '—',
        initials: session?.initials ?? '··',
        jobTitle: session?.jobTitle ?? undefined,
      }}
      fx={{
        currency,
        currencies,
        rate: rateFor(currency),
        onCurrencyChange: setCurrency,
        onRateChange: (rate) => setRates((prior) => ({ ...prior, [currency]: rate })),
        canEditRate: isAdmin,
        onApplyRate: applyRate,
      }}
      branch={{
        options: session?.branches.map((b) => ({ code: b.code, name: b.name })) ?? [],
        active: branchCode,
        onChange: setBranchCode,
        canSwitch: canSwitchBranch,
      }}
      search={{ find: searchHits, onSelect: openSearchHit }}
      roles={viewAs}
      activeRole={session?.email}
      onRoleChange={impersonator ? switchView : undefined}
      onSignOut={handleSignOut}
    >
      {sessionRes.error ? (
        <ApiDown message={sessionRes.error} />
      ) : (
        <Screen
          item={item}
          module={module}
          session={session}
          accounts={accounts}
          currency={currency}
          fxRate={rateFor(currency)}
          currencies={currencies}
          baseCurrency={baseCurrency}
          usdRate={rateFor('USD')}
          branchCode={branchCode}
          ledgerAccount={ledgerAccount || defaultTill}
          onLedgerAccountChange={setLedgerAccount}
          dealNo={dealNo}
          onDealChange={setDealNo}
          onOpenDeal={openDeal}
          shipmentNo={shipmentNo}
          onShipmentChange={setShipmentNo}
          onOpenCosting={openCosting}
          onOpenLedger={openLedger}
          onOpenItem={openItem}
          onNavigate={navigate}
          onChartChanged={accountsRes.reload}
        />
      )}

      <VoucherDetailModal
        voucherType={openVoucher?.type ?? null}
        voucherNo={openVoucher?.no ?? null}
        onClose={() => setOpenVoucher(null)}
        onOpenLedger={(code) => {
          setOpenVoucher(null);
          openLedger(code);
        }}
      />
    </AppShell>
  );
}

const GROUP_LABEL: Record<ApiSearchHit['kind'], string> = {
  account: 'Accounts',
  voucher: 'Vouchers',
};

interface ApiSearchHit {
  kind: 'account' | 'voucher';
  key: string;
  label: string;
  hint: string | null;
  voucherType: string | null;
}

function Screen({
  item,
  module,
  session,
  accounts,
  currency,
  fxRate,
  currencies,
  baseCurrency,
  usdRate,
  branchCode,
  ledgerAccount,
  onLedgerAccountChange,
  dealNo,
  onDealChange,
  onOpenDeal,
  shipmentNo,
  onShipmentChange,
  onOpenCosting,
  onOpenLedger,
  onOpenItem,
  onNavigate,
  onChartChanged,
}: {
  item: string;
  module: string;
  session: Session | undefined;
  accounts: Account[];
  currency: EntryCurrency;
  fxRate: string;
  currencies: CurrencyOption[];
  baseCurrency: string;
  usdRate: string;
  branchCode: string;
  ledgerAccount: string;
  onLedgerAccountChange: (code: string) => void;
  dealNo: string | null;
  onDealChange: (dealNo: string | null) => void;
  onOpenDeal: (dealNo: string) => void;
  shipmentNo: string | null;
  onShipmentChange: (shipmentNo: string | null) => void;
  onOpenCosting: (shipmentNo: string) => void;
  onOpenLedger: (code: string) => void;
  onOpenItem: (key: string) => void;
  onNavigate: (module: string, item: string) => void;
  onChartChanged: () => void;
}) {
  switch (item) {
    case 'overview':
      return (
        <DashboardScreen
          userName={firstNameOf(session)}
          role={roleOf(session)}
          onOpenItem={onNavigate}
        />
      );
    case 'coa':
      return <ChartOfAccountsScreen onOpenLedger={onOpenLedger} onChartChanged={onChartChanged} />;
    case 'gl':
      return (
        <GeneralLedgerScreen accountCode={ledgerAccount} onAccountChange={onLedgerAccountChange} />
      );
    case 'tb':
      return <TrialBalanceScreen onOpenLedger={onOpenLedger} />;
    case 'pl':
      return <ProfitAndLossScreen />;
    case 'bs':
      return <BalanceSheetScreen />;
    case 'deal':
      return (
        <DealEntryScreen branchCode={branchCode} dealNo={dealNo} onDealChange={onDealChange} />
      );
    case 'deal-register':
      return <DealRegisterScreen onOpenDeal={onOpenDeal} />;
    case 'deal-parties':
      return <DealPartiesScreen />;
    case 'availability':
      return <AvailabilityScreen />;
    case 'commission-invoice':
      return <CommissionInvoiceScreen />;
    case 'settlement':
      return <PendingSettlementScreen />;
    case 'shipments':
      return <ImportShipmentsScreen onOpenCosting={onOpenCosting} sessionFxRate={usdRate} />;
    case 'costing':
      return <ImportCostingScreen shipmentNo={shipmentNo} onShipmentChange={onShipmentChange} />;
    case 'purchase':
      return <PurchaseEntryScreen />;
    case 'preturn':
      return <PurchaseReturnScreen />;
    case 'invoice':
      return <SalesInvoiceScreen />;
    case 'sreturn':
      return <SaleReturnScreen />;
    case 'commission-run':
      return <CommissionRunScreen />;
    case 'parties':
      return <PartiesScreen />;
    case 'products':
      return <ProductsScreen />;
    case 'categories':
      return <CategoriesScreen />;
    case 'stock':
      return <WarehouseStockScreen />;
    case 'stock-ledger':
      return <StockLedgerScreen />;
    case 'transfer':
      return <StockTransferScreen />;
    case 'count':
      return <PhysicalCountScreen />;
    case 'batch':
      return <BatchExpiryScreen />;
    case 'hierarchy':
    case 'roles':
    case 'zones':
      return <UsersZonesScreen focus={item} />;
    case 'company':
    case 'fy':
    case 'numbering':
    case 'fx':
    case 'branches':
    case 'currencies':
    case 'uoms':
    case 'links':
      return <SetupScreen section={item as SetupKey} />;
    case 'cash-position':
      return <CashPositionScreen />;
    case 'recon':
      return <BankReconciliationScreen />;
    case 'receivables':
      return <ArAgeingScreen />;
    case 'payables':
      return <ApAgeingScreen />;
    case 'rpt-ageing':
      return <ArApAgeingScreen />;
    case 'rpt-saleszone':
      return <SalesByZoneScreen />;
    case 'rpt-margin':
      return <GrossMarginScreen />;
    case 'rpt-sales-register':
      return <SalesRegisterScreen />;
    case 'rpt-purchase-register':
      return <PurchaseRegisterScreen />;
    case 'rpt-audit':
      return <AuditTrailScreen />;
    case 'activity':
      return <RecentActivityScreen />;
    case 'library':
      return <ReportsScreen onOpen={onOpenItem} />;
    default:
      break;
  }

  if (isVoucherTypeCode(item)) {
    return (
      <VoucherEntryScreen
        key={item}
        type={item.toUpperCase() as VoucherTypeCode}
        role={roleOf(session)}
        currency={currency}
        fxRate={fxRate}
        baseCurrency={baseCurrency}
        branchCode={branchCode}
        accounts={accounts}
      />
    );
  }

  if (RECORD_SETS[item]) return <RecordsScreen key={item} recordKey={item} />;

  return <Pending module={module} item={item} />;
}

interface DirectoryUser {
  email: string;
  fullName: string;
  roleName: string;
  scopeName: string;
}

function firstNameOf(session: Session | undefined): string {
  return session?.fullName?.split(' ')[0] ?? 'there';
}

function roleOf(session: Session | undefined): Role {
  if (!session) return 'accountant';
  if (session.isSuperAdmin || session.roleCode === 'OWNER' || session.roleCode === 'ADMIN') {
    return 'owner';
  }
  return session.roleCode === 'MANAGER' ? 'manager' : 'accountant';
}

function toAccount(account: ApiAccount): Account {
  return {
    code: account.code,
    name: account.name,
    type: account.type,
    normalBalance: account.normalBalance,
    control: (account.controlKind as Account['control']) ?? null,
    allowDirectPosting: account.allowDirectPosting,
    fcyCurrency: account.fcyCurrency,
    requiresParty: account.requiresParty || isSubLedger(account.controlKind),
    isLeaf: account.isLeaf,
    level: account.level,
    parentCode: account.parentCode,
    childCount: account.childCount,
    balanceBase: account.balanceBase,
    isBank: account.isBank,
  };
}

function isSubLedger(controlKind: string | null): boolean {
  return (
    controlKind === 'RECEIVABLE' ||
    controlKind === 'PAYABLE' ||
    controlKind === 'COMMISSION_RECEIVABLE' ||
    controlKind === 'COMMISSION_PAYABLE'
  );
}

function ApiDown({ message }: { message: string }) {
  return (
    <>
      <PageHeader
        breadcrumb="biovet Accounting"
        title="The API is not answering"
        description="The application cannot reach the server, so there are no figures to show."
      />
      <Card flush>
        <CardHeader title="Start the stack" subtitle="Three commands, one at a time" />
        <CardBody className="flex flex-col gap-2.5">
          <Callout tone="danger" title="What the browser saw">
            {message}
          </Callout>
          <Callout tone="info" title="1 · Database">
            <code className="bb-mono">pnpm infra:up</code> then{' '}
            <code className="bb-mono">pnpm db:migrate</code> and{' '}
            <code className="bb-mono">pnpm db:seed</code>. Docker Desktop must be running.
          </Callout>
          <Callout tone="info" title="2 · API">
            <code className="bb-mono">pnpm api</code> — serves on 3001, which Vite proxies.
          </Callout>
          <Callout tone="info" title="3 · This app">
            <code className="bb-mono">pnpm dev</code>
          </Callout>
        </CardBody>
      </Card>
    </>
  );
}

function Pending({ module, item }: { module: string; item: string }) {
  const mod = MODULES.find((m) => m.key === module);
  const nav = mod?.items.find((i) => i.key === item);

  return (
    <>
      <PageHeader
        breadcrumb={`${mod?.label ?? ''} / ${nav?.label ?? ''}`}
        title={nav?.label ?? 'Screen'}
        description="This screen is planned and has not been built yet."
      />

      <Card flush>
        <CardHeader
          title="What already exists behind this screen"
          subtitle="Schema, invariants and reporting objects are live"
        />
        <CardBody className="flex flex-col gap-2.5">
          <Callout tone="success" title="Database">
            Tables, constraints and PL/pgSQL invariants are written and tested. Run{' '}
            <code className="bb-mono">pnpm db:test</code> to see the four ledger invariants enforced
            against real postings.
          </Callout>
          <Callout tone="warn" title="Not yet built">
            This screen&rsquo;s endpoint and form, the NestJS API and the CASL authorisation layer.
            See <code className="bb-mono">docs/roadmap.md</code>.
          </Callout>
        </CardBody>
      </Card>
    </>
  );
}
